<script setup lang="ts">
import { FallbackAttachmentType, fallbackAttachmentSizeMapping } from '@@/bits/attachments'
import { ContentPickerSource } from '@@/bits/content_picker'
import { dir } from '@@/bits/current_dir'
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { recordWhitelistedClick } from '@@/bits/pepin'
import { updatePollShapeSize } from '@@/bits/whiteboard'
import { getEditorObject } from '@@/bits/tldraw/editor'
import { blankUser } from '@@/bits/user_model'
import { setupVueApp } from '@@/bits/vue'
import {
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  WHITEBOARD_ATTACHMENT_PADDING,
  shouldEnableWhiteboardInPlaceUploadingAnimation,
} from '@@/bits/whiteboard_no_dependencies'
import { calculateUploadedFilePositionsAndDimensions } from '@@/bits/whiteboard_upload'
import { useContentPickerStore } from '@@/pinia/content_picker'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceAttachmentsStore } from '@@/pinia/surface_attachments'
import { useSurfaceContentPickerStore } from '@@/pinia/surface_content_picker'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfaceRequireNoticeStore } from '@@/pinia/surface_require_notice_store'
import { useWhiteboardElementModifierStore } from '@@/pinia/whiteboard_element_modifier'
import { useWhiteboardUploadStore } from '@@/pinia/whiteboard_upload'
import type { Post, PostAttributes } from '@@/types'
import ContentPicker from '@@/vuecomponents/ContentPicker.vue'
import SurfacePostPoll from '@@/vuecomponents/SurfacePostPoll.vue'
import { usePostContentPickerSources } from '@@/vuecomposables/post_content_picker_sources'
import { usePostDraftPublisher } from '@@/vuecomposables/usePostDraftPublisher'
import { Portal } from '@linusborg/vue-simple-portal'
import { LinkHelpers, getDisplayAttributes } from '@padlet/beethoven-client'
import type { PadletAttachmentShape } from '@padlet/whiteboard-schema'
import { uniqueId } from 'es-toolkit/compat'
import { storeToRefs } from 'pinia'
import { createShapeId, type Editor, type JsonObject } from 'tldraw'
import { computed, toRef, watch } from 'vue'

// Content picker
const contentPickerStore = useContentPickerStore()
const surfaceContentPickerStore = useSurfaceContentPickerStore()
const surfaceStore = useSurfaceStore()
const surfaceRequireNoticeStore = useSurfaceRequireNoticeStore()
const whiteboardUploadStore = useWhiteboardUploadStore()
const { contentSources } = usePostContentPickerSources()
const surfaceDraftsStore = useSurfaceDraftsStore()
const whiteboardElementModifierStore = useWhiteboardElementModifierStore()

const { xPostContentPicker } = storeToRefs(surfaceContentPickerStore)
const googleDriveUploadKey = uniqueId('google-drive-upload-key-')
const xSearchBar = computed(() => {
  return contentSources.value
    .flat()
    .filter((source) => source.visible)
    .some((source) =>
      [
        ContentPickerSource.ImageSearch,
        ContentPickerSource.GifSearch,
        ContentPickerSource.VideoSearch,
        ContentPickerSource.AudioSearch,
        ContentPickerSource.WebBrowser,
        ContentPickerSource.LinkInput,
      ].includes(source.type as ContentPickerSource),
    )
})

const xContentPicker = computed(() => {
  return (
    (contentPickerStore.xContentSourceMenu === true || contentPickerStore.activeContentPickerPanel !== null) &&
    surfaceContentPickerStore.xPostContentPicker
  )
})

function hideWhiteboardContentPicker(): void {
  contentPickerStore.hideContentPickerPanel()
  surfaceContentPickerStore.hidePostContentPicker()
}

const surfaceAttachmentStore = useSurfaceAttachmentsStore()
const surfacePostsStore = useSurfacePostsStore()

const { newestPostCid } = storeToRefs(surfacePostsStore)
const newestPost = computed(() => surfacePostsStore.postEntitiesByCid[newestPostCid.value])
const newestPostId = computed(() => newestPost.value?.id)

function onUploadReject(): void {
  // TODO: Implement
}

function onUploadError(): void {
  // TODO: Implement
}

function onUploadProgress(): void {
  // Ignore for now
}

function updateActiveDraft(postAttributes: PostAttributes, contentSource: ContentPickerSource): void {
  if (
    shouldEnableWhiteboardInPlaceUploadingAnimation() &&
    typeof postAttributes.attachment === 'string' &&
    postAttributes.wish_content?.attachment_props?.type !== 'poll'
  ) {
    // Start new upload for AI talk attachment
    whiteboardUploadStore.startNewUrlUpload(postAttributes.attachment, { contentSource })
  } else if (surfaceDraftsStore.activeDraft && postAttributes.wish_content?.attachment_props.edit_poll === true) {
    updatePoll(postAttributes)
  } else {
    surfacePostsStore.createPostImmediately({
      attributes: {
        ...postAttributes,
      },
    })
  }
}

async function uploadFileActiveDraft(payload: {
  file: File
  contentSource: ContentPickerSource
  trimVideoAfterUpload?: { start: number; end: number }
}): Promise<void> {
  if (shouldEnableWhiteboardInPlaceUploadingAnimation()) {
    const filePositionsAndDimensions = await calculateUploadedFilePositionsAndDimensions({
      files: [payload.file],
    })
    const { width, height } = filePositionsAndDimensions[0]
    whiteboardUploadStore.startNewFileUpload(payload.file, {
      contentSource: payload.contentSource,
      trimVideoAfterUpload: payload.trimVideoAfterUpload,
      width,
      height,
      x: CANVAS_WIDTH / 2 - width / 2, // Center the shape
      y: CANVAS_HEIGHT / 2 - height / 2, // Center the shape
    })
  } else {
    whiteboardUploadStore.startNewFileUploadOld(payload.file, {
      contentSource: payload.contentSource,
      trimVideoAfterUpload: payload.trimVideoAfterUpload,
    })
  }
}

function onUploadStart(payload): void {
  // Not being triggered but still need to pass the prop
  // Real upload is done in uploadFileActiveDraft
}

async function onUploadDone(url: string): Promise<void> {
  surfacePostsStore.createPostImmediately({
    attributes: {
      attachment: url,
      wish_content: {
        attachment_props: {
          url,
        },
        is_processed: false,
      },
    },
  })
}

function showExtensionRequired(): void {
  surfaceRequireNoticeStore.showExtensionRequired()
  surfaceRequireNoticeStore.addAfterHideExtensionRequiredAction(() => {
    hideWhiteboardContentPicker()
  })
}

function setCaption(): void {
  // TODO: Implement
}

function selectLink(url: string, payload: { altText?: string; contentSource: ContentPickerSource }): void {
  if (shouldEnableWhiteboardInPlaceUploadingAnimation()) {
    whiteboardUploadStore.startNewUrlUpload(url, payload)
  } else {
    whiteboardUploadStore.startNewUrlUploadOld(url, payload)
  }
}

// Only after the newest post is created, we can create the attachment shape in TLDraw
watch(newestPostId, async () => {
  if (!newestPostId.value) return
  const editor = getEditorObject()
  if (!editor) return
  const post = newestPost.value
  if (!post) {
    throwErrorForMissingDataInNewestPost()
    return
  }
  if (post.id == null) {
    throwErrorForMissingDataInNewestPost()
    return
  }
  if (post.top != null) {
    throwErrorForMissingDataInNewestPost()
    return
  }
  // Create post for attachment
  if (post.attachment || post.wish_content?.attachment_props?.url) {
    if (!shouldEnableWhiteboardInPlaceUploadingAnimation()) {
      createPostForAttachment(editor, post)
    }
  } else if (post.wish_content?.attachment_props.type === 'poll') {
    createPostForPoll(editor, post)
  }
})

function throwErrorForMissingDataInNewestPost(): void {
  throw new Error('Missing data in newest post')
}

async function createPostForAttachment(editor: Editor, post: Post): Promise<void> {
  const url: string = post.wish_content?.attachment_props?.url ?? post.attachment
  const link = await surfaceAttachmentStore.fetchAndStoreLinkAttributes(url) // Eagerly fetch the link
  if (!link) {
    throwErrorForMissingDataInNewestPost()
    return
  }
  const attributes = await getDisplayAttributes(link, {
    width: CANVAS_WIDTH / 2,
  })
  if (!attributes) {
    throwErrorForMissingDataInNewestPost()
    return
  }
  let width = attributes.image_width
  let height = attributes.image_height
  if (width == null || height == null) {
    throwErrorForMissingDataInNewestPost()
    return
  }

  let ar = attributes.image_aspect_ratio
  if (attributes.is_fallback) {
    const fallbackAttachmentType = (() => {
      // if (isBrokenPadletLink.value || isFallbackPeekLink.value) return FallbackAttachmentType.BrokenPadletLink
      if (!link) return FallbackAttachmentType.BrokenLink
      if (LinkHelpers.isAudio(link)) return FallbackAttachmentType.Audio
      if (LinkHelpers.isVideo(link)) return FallbackAttachmentType.Video
      if (LinkHelpers.isPhoto(link)) return FallbackAttachmentType.Image
      if (LinkHelpers.isFile(link)) return FallbackAttachmentType.File
      if (LinkHelpers.isExcelSpreadsheet(link)) return FallbackAttachmentType.Spreadsheet
      if (LinkHelpers.isPowerpointPresentation(link)) return FallbackAttachmentType.Presentation
      if (LinkHelpers.isGoogleDoc(link)) return FallbackAttachmentType.GoogleDocument
      if (LinkHelpers.isDocument(link)) return FallbackAttachmentType.TextDocument
      if (LinkHelpers.isPage(link)) return FallbackAttachmentType.Link
      return FallbackAttachmentType.Link
    })()

    ar =
      fallbackAttachmentSizeMapping[fallbackAttachmentType].width /
      fallbackAttachmentSizeMapping[fallbackAttachmentType].height
  }
  if (link?.provider_name?.toLowerCase() === 'youtube') {
    ar = 16 / 9
  }
  height = width / ar

  if (width > CANVAS_WIDTH / 2) {
    width = CANVAS_WIDTH / 2
    height = Math.floor(width / ar)
  }
  if (height > CANVAS_HEIGHT / 2) {
    height = CANVAS_HEIGHT / 2
    width = Math.floor(height * ar)
  }

  // See `native_bridge_listeners.ts#installWhiteboardContentPickerListeners#add_whiteboard_attachment`
  // for context why we're deserializing the data from `post.subject`.
  let contentSource: ContentPickerSource | null = null
  let mimeType: string | null = null
  try {
    const deserialized = JSON.parse(post.subject ?? '{}')
    contentSource = deserialized.sourceType ?? null
    mimeType = deserialized.mimeType ?? null
  } catch {
    // Gracefully handle when deserialization fails.
    // We were passing `sourceType` in `post.subject` before so we'll default to that.
    contentSource = (post.subject as ContentPickerSource | undefined) ?? null
  }

  const isAudioRecording = contentSource === ContentPickerSource.AudioRecorder
  const isAudioUpload = mimeType?.startsWith('audio/') ?? false

  const meta: Partial<JsonObject> = {
    contentSource,
  }

  if (isAppUsing('audioPlayerV2Sandbox') && (isAudioRecording || isAudioUpload)) {
    meta.audioPlayerVersion = 2
  }

  editor.createShape({
    type: 'padlet-attachment-shape',
    x: CANVAS_WIDTH / 2 - width / 2,
    y: CANVAS_HEIGHT / 2 - height / 2,
    props: {
      postId: post.id,
      w: width + WHITEBOARD_ATTACHMENT_PADDING * 2,
      h: height + WHITEBOARD_ATTACHMENT_PADDING * 2,
    },
    meta,
  })

  surfacePostsStore.savePost({
    attributes: {
      ...post,
      // Now that we're done with the data in `post.subject`, we can remove it.
      subject: '',
      top: CANVAS_HEIGHT / 2 - height / 2,
      left: CANVAS_WIDTH / 2 - width / 2,
    },
  })
  whiteboardUploadStore.finishUploadingOld(url)
}

async function createPostForPoll(editor: Editor, post: Post) {
  if (!post) {
    throwErrorForMissingDataInNewestPost()
    return
  }
  const aspectRatio = 1.9
  const { height } = await precalculateSizeForPoll(post)

  const id = createShapeId()
  editor.createShape({
    id,
    type: 'padlet-attachment-shape',
    x: CANVAS_WIDTH / 2 - CANVAS_WIDTH / 3 / 2,
    y: CANVAS_HEIGHT / 2 - CANVAS_HEIGHT / 3 / 2,
    props: {
      postId: post.id,
      w: height * aspectRatio + WHITEBOARD_ATTACHMENT_PADDING * 2,
      h: height + WHITEBOARD_ATTACHMENT_PADDING * 2,
      isPoll: true,
    },
    meta: {
      contentSource: ContentPickerSource.Poll,
    },
  })
  await surfacePostsStore.savePost({
    attributes: {
      ...post,
      top: CANVAS_HEIGHT / 2 - CANVAS_WIDTH / 3 / 2,
      left: CANVAS_WIDTH / 2 - CANVAS_HEIGHT / 3 / 2,
    },
  })

  // `precalculateSizeForPoll` can be incorrect. Attempt to resize
  // the poll shape after it has been created to get the correct size.
  // Try this 3 times as the shape might not render yet. If the size
  // is still incorrect after 3 times, the user can manually resize.
  await updatePollShapeSize(id, { maxTries: 3 })
}

async function updatePoll(postAttributes: PostAttributes): Promise<void> {
  const shape = whiteboardElementModifierStore.onlySelectedShape as PadletAttachmentShape
  if (!surfaceDraftsStore.activeDraft || shape == null) return
  // Update and publish the draft
  surfaceDraftsStore.updateDraft({
    ...surfaceDraftsStore.activeDraft,
    wish_content: {
      ...surfaceDraftsStore.activeDraft.wish_content,
      attachment_props: {
        ...surfaceDraftsStore.activeDraft.wish_content?.attachment_props,
        ...postAttributes.wish_content?.attachment_props,
      },
    },
  })
  const draftCid = toRef(surfaceDraftsStore.activeDraft, 'cid')
  const { publishCurrentDraft } = usePostDraftPublisher({
    draftCid,
  })
  publishCurrentDraft()

  await updatePollShapeSize(shape.id)
}

async function precalculateSizeForPoll(post: Post): Promise<{ width: number; height: number }> {
  // Render SurfacePostPoll for poll to get the size
  const poll = post.wish_content?.attachment_props
  if (!poll) {
    throwErrorForMissingDataInNewestPost()
    return { width: 0, height: 0 }
  }

  // Poll width is 1/3 of the canvas width by default
  const pollWidth = CANVAS_WIDTH / 3

  /**
   * Calculate poll height
   */

  // Create element on Dom
  const pollEl = document.createElement('div')
  pollEl.id = uniqueId('poll-')
  const pollVueApp = document.createElement('div')
  pollVueApp.id = uniqueId('poll-vue-app-')
  pollEl.setAttribute(
    'style',
    `width: ${pollWidth}px; visibility: hidden; position: absolute; top: -9999px; left: -9999px;`,
  )
  pollEl.appendChild(pollVueApp)
  document.body.appendChild(pollEl)

  // Render poll
  const temporaryPollApp = await setupVueApp({
    el: `#${pollVueApp.id}`,
    usePinia: false,
    disableAccessibilityChecks: true,
    rootComponent: SurfacePostPoll,
    otherVueOptions: {
      parent: (window as any).app,
      render: function (h: any) {
        return h(SurfacePostPoll, {
          props: {
            poll,
            pollCaption: post.attachment_caption,
            post,
            user: blankUser,
            darkMode: true,
            displayMode: 'slideshow',
          },
        })
      },
      methods: {
        handleOnLoaded: function () {},
      },
    },
  })
  // Get rendered height
  const pollHeight = pollEl.clientHeight

  // Clean up
  temporaryPollApp?.$destroy()
  document.body.removeChild(pollEl)

  // Return size
  return { width: pollWidth, height: pollHeight }
}
</script>

<template>
  <div
    :dir="dir()"
    :class="xPostContentPicker ? 'z-[30004]' : 'z-addpost'"
    @click.stop="recordWhitelistedClick"
    @keydown.stop="() => {}"
  >
    <Portal v-if="xContentPicker" selector="#content-picker-portal-target">
      <ContentPicker
        :content-picker-context="'whiteboardUpload'"
        :enable-hidden-inputs="device.mobile"
        :preferred-content-source-menu-start-height="500"
        :content-source-menu-list="contentSources"
        :x-search-bar="xSearchBar"
        :is-online="surfaceStore.isOnline"
        :search-modes="surfaceContentPickerStore.currentSearchModes"
        :default-search-mode="surfaceContentPickerStore.defaultSearchMode"
        :search-suggestions="surfaceContentPickerStore.searchSuggestions"
        :google-drive-upload-key="googleDriveUploadKey"
        :user="surfaceStore.user"
        :should-upload-file="false"
        :wall="surfaceStore.wallAttributes"
        :active-draft="surfaceDraftsStore.activeDraft"
        @close-content-picker="hideWhiteboardContentPicker"
        @upload-start="onUploadStart"
        @upload-done="onUploadDone"
        @upload-reject="onUploadReject"
        @upload-error="onUploadError"
        @upload-progress="onUploadProgress"
        @select-link="selectLink"
        @update-active-draft="updateActiveDraft"
        @select-file="uploadFileActiveDraft"
        @show-extension-required="showExtensionRequired"
        @set-caption="setCaption"
      />
    </Portal>
  </div>
</template>
